import { useRef, useState } from "react";
import { speakText, stopSpeaking } from "@/lib/voice";
import { Volume2, Loader2, Square } from "lucide-react";
import { toast } from "sonner";

// Split long text into speakable chunks (~2000 chars) at sentence boundaries.
function chunkText(text, size = 2000) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return [];
  const chunks = [];
  let i = 0;
  while (i < clean.length) {
    let end = Math.min(i + size, clean.length);
    if (end < clean.length) {
      const slice = clean.slice(i, end);
      const stop = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("! "), slice.lastIndexOf("? "), slice.lastIndexOf("; "));
      if (stop > size * 0.5) end = i + stop + 1;
    }
    chunks.push(clean.slice(i, end).trim());
    i = end;
  }
  return chunks;
}

// Ensures only one read-aloud plays at a time across the whole app.
let activeStop = null;
function registerStop(fn) { if (activeStop && activeStop !== fn) { try { activeStop(); } catch {} } activeStop = fn; }
function clearStop(fn) { if (activeStop === fn) activeStop = null; }

// A small button that has Anubis (ElevenLabs TTS) read the given text aloud, hands-free.
// Pass either `text` (string) or `getText` (function returning a string).
export function ReadAloud({ getText, text, label = "Read aloud", className = "" }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const cancelRef = useRef(false);

  const stop = () => {
    cancelRef.current = true;
    stopSpeaking();
    setState("idle");
  };

  const play = async () => {
    if (state !== "idle") { stop(); return; }
    const raw = typeof getText === "function" ? getText() : text;
    const chunks = chunkText(raw);
    if (!chunks.length) { toast("Nothing to read here yet."); return; }
    cancelRef.current = false;
    registerStop(stop);
    setState("loading");
    const voiceId = localStorage.getItem("oracle_voice_id") || undefined;
    const speed = parseFloat(localStorage.getItem("oracle_speed")) || 1.0;
    try {
      for (let idx = 0; idx < chunks.length; idx++) {
        if (cancelRef.current) return;
        setState("playing");
        await speakText(chunks[idx], { voiceId, speed, isCanceled: () => cancelRef.current });
      }
    } finally {
      clearStop(stop);
      if (!cancelRef.current) setState("idle");
    }
  };

  const Icon = state === "loading" ? Loader2 : state === "playing" ? Square : Volume2;
  return (
    <button
      type="button"
      onClick={play}
      data-testid="read-aloud-btn"
      title={state === "idle" ? "Have Anubis read this aloud" : "Stop reading"}
      className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${state !== "idle" ? "text-primary" : "text-muted-foreground hover:text-primary"} ${className}`}
    >
      <Icon className={`w-4 h-4 ${state === "loading" ? "animate-spin" : ""}`} />
      {label && <span>{state === "playing" ? "Stop" : state === "loading" ? "Loading…" : label}</span>}
    </button>
  );
}

export default ReadAloud;
